import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { DocumentIcon, DownloadIcon } from "@heroicons/react/outline";
import { downloadData, getUrl } from "aws-amplify/storage";
import dayjs from "dayjs";

import AccountingWrapper from "../../../../Components/v2/Accounting/AccountingWrapper";
import AccountingPageHeader from "../../../../Components/v2/Accounting/AccountingPageHeader";
import ReportRevisionNotice from "../../../../Components/v2/Accounting/ReportRevisionNotice";
import TableSkeleton from "../../../../Components/TableSkeleton";
import ErrorStateTable from "../../../../Components/ErrorStateTable";
import Modal from "../../../../Components/v2/Modal";
import FormDateRange from "../../../../Components/v2/Form/FormDateRange";

import {
  cancelAPIRequest,
  convertToTitleCase,
  getAccountingFileName,
} from "../../../../Utils";
import {
  clearExportCashflow,
  requestCashFlow,
  requestExportCashflow,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
} from "../../../../Context";
import {
  cashFlowAccountCategoryLabel,
  EXPORTS,
} from "../../../../Constants/accountingReport";
import {
  wibCurrentMonth,
  wibEndOfDay,
  wibStartOfDay,
} from "../../../../Utils/PeriodUtils";
import ErrorDialog from "../../../../Components/v2/ErrorDialog";

const AccountingCashFlowReport = () => {
  const { t } = useTranslation();
  const { user } = useAuthState();

  const [dateRange, setDateRange] = useState(wibCurrentMonth);
  const [startDate, endDate] = dateRange;
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const downloadRef = useRef(null);
  const [downloadDocError, setDownloadDocError] = useState(null);
  const [showDownloadErrorModal, setShowDownloadErrorModal] = useState(false);
  const [showExportError, setShowExportError] = useState(false);

  const accountingDispatch = useAccountingDispatch();

  const {
    request,
    cashFlowItems: storedItems,
    cashFlow: storedCashFlow,
    previousDate: storedPreviousDate,
    previousCash: storedPreviousCash,
    endDate: storedEndCashDate,
    endCash: storedEndCash,
    cashFlowLoading,
    cashFlowError,
    cashFlowExportFile,
    cashFlowExportLoading,
    cashFlowExportError,
    cashFlowNonCash: storedNonCash,
  } = useAccountingState();

  // With the range cleared nothing is requested, so the store still holds the
  // previous period. Show nothing rather than present it under an empty picker.
  const hasRange = Boolean(startDate && endDate);
  const cashFlowItems = hasRange ? storedItems : [];
  const cashFlow = hasRange ? storedCashFlow : null;
  const previousDate = hasRange ? storedPreviousDate : null;
  const previousCash = hasRange ? storedPreviousCash : null;
  const endCashDate = hasRange ? storedEndCashDate : null;
  const endCash = hasRange ? storedEndCash : null;
  const cashFlowNonCash = hasRange ? storedNonCash : [];

  const nonCashActivities = cashFlowNonCash ?? [];

  const items = cashFlowItems ?? [];

  const calculateTotal = (category) =>
    items
      .filter((item) => item?.category === category)
      .reduce((sum, item) => sum + item.total, 0);

  const operationalTotal = calculateTotal("OPERATIONAL");
  const investmentTotal = calculateTotal("INVESTMENT");
  const financingTotal = calculateTotal("FINANCING");

  const operationalData = items.filter(
    (item) => item?.category === "OPERATIONAL",
  );
  const investmentData = items.filter(
    (item) => item?.category === "INVESTMENT",
  );
  const financingData = items.filter((item) => item?.category === "FINANCING");

  // --- Effects ---
  useEffect(() => {
    return () => {
      if (request) cancelAPIRequest(request);
      clearExportCashflow(accountingDispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user.mainBusiness?.id) return;
    if ((startDate && !endDate) || (!startDate && endDate)) return;
    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.mainBusiness?.id, startDate, endDate]);

  useEffect(() => {
    if (cashFlowExportFile?.key) {
      downloadFile(cashFlowExportFile.key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cashFlowExportFile]);

  useEffect(() => {
    if (cashFlowExportError) {
      setShowExportError(true);
    }
  }, [cashFlowExportError]);

  // --- Handlers ---
  const onLoad = () => {
    if (request) cancelAPIRequest(request);
    if (user.mainBusiness?.id && startDate && endDate) {
      requestCashFlow(accountingDispatch, {
        businessID: user.mainBusiness.id,
        startDate: wibStartOfDay(startDate),
        endDate: wibEndOfDay(endDate),
      });
    }
  };

  const handleExport = useCallback(
    (exportType) => {
      if (!startDate || !endDate) return;
      if (user.mainBusiness?.id) {
        requestExportCashflow(accountingDispatch, {
          businessID: user.mainBusiness.id,
          startDate: wibStartOfDay(startDate),
          endDate: wibEndOfDay(endDate),
          exportType,
        });
      }
    },
    [accountingDispatch, endDate, startDate, user.mainBusiness?.id],
  );

  const onDownloadProgress = (progress) => {
    const { transferredBytes, totalBytes } = progress;
    setDownloadProgress(totalBytes ? transferredBytes / totalBytes : 0);
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "download";
    const clickHandler = () => {
      setTimeout(() => {
        URL.revokeObjectURL(url);
        a.removeEventListener("click", clickHandler);
      }, 150);
    };
    a.addEventListener("click", clickHandler, false);
    a.click();
    return a;
  };

  const downloadFile = async (key) => {
    try {
      setDownloading(true);
      const req = downloadData({
        path: ({ identityId }) => `private/${identityId}/${key}`,
        options: { onProgress: onDownloadProgress },
      });
      downloadRef.current = req;
      const result = await req.result;
      downloadBlob(result.body, getAccountingFileName(key));
    } catch (downloadError) {
      if (downloadError.message !== "canceled") {
        await handleDownloadError(key);
      }
    } finally {
      setDownloading(false);
      setDownloadProgress(0);
      clearExportCashflow(accountingDispatch);
    }
  };

  const handleDownloadError = async (key) => {
    try {
      const urlResult = await getUrl({
        path: ({ identityId }) => `private/${identityId}/${key}`,
        options: { useAccelerateEndpoint: true },
      });
      window.open(urlResult.url.href);
    } catch (signedURLError) {
      setDownloadDocError(signedURLError);
      setShowDownloadErrorModal(true);
    }
  };

  const exportActionItems = EXPORTS.map((item) => ({
    name: t(item.label),
    onClick: () => handleExport(item.value),
  }));

  // --- Table helpers ---
  const TableSection = ({ categoryName, totalAmount }) => (
    <>
      <tr className="bg-gray-50">
        <th className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap text-left">
          {t(convertToTitleCase(categoryName))}
        </th>
        <th className="px-4 py-2 font-medium text-sm text-right text-gray-900 whitespace-nowrap">
          {t("Amount")}
        </th>
      </tr>
      {items
        .filter((transaction) => transaction?.category === categoryName)
        .map((transaction, idx) => {
          const accounts = transaction.accounts || [];
          return accounts.map((acc, index) => {
            const { category, total } = acc;
            return (
              <tr key={`${idx}-${index}`}>
                <td className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap">
                  {t(cashFlowAccountCategoryLabel(category))}
                </td>
                <td className="px-4 py-2 text-sm text-right text-gray-700 whitespace-nowrap">
                  <NumericFormat
                    displayType="text"
                    value={total}
                    thousandSeparator="."
                    decimalSeparator=","
                    decimalScale={2}
                    prefix="Rp"
                  />
                </td>
              </tr>
            );
          });
        })}
      <tr className="bg-gray-50">
        <th className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap">
          {`Total ${t(convertToTitleCase(categoryName))}`}
        </th>
        <td className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap text-right">
          <NumericFormat
            displayType="text"
            value={totalAmount}
            thousandSeparator="."
            decimalSeparator=","
            decimalScale={2}
            prefix="Rp"
          />
        </td>
      </tr>
    </>
  );

  const EmptyTableSection = ({ categoryName, totalAmount }) => (
    <tr className="bg-gray-50">
      <th className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap text-left">
        {`Total ${t(convertToTitleCase(categoryName))}`}
      </th>
      <td className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap text-right">
        <NumericFormat
          displayType="text"
          value={totalAmount}
          thousandSeparator="."
          decimalSeparator=","
          decimalScale={2}
          prefix="Rp"
        />
      </td>
    </tr>
  );

  return (
    <AccountingWrapper>
      <main className="lg:col-span-9">
        <AccountingPageHeader
          title="Cash Flow"
          dropDownAction={{
            label: t("Export"),
            icon: <DownloadIcon className="h-5 w-5" />,
            onClick: () => handleExport("pdf"),
            items: exportActionItems,
            disabled: cashFlowExportLoading,
          }}
        />
        {/* The filter range, not the `endDate` from context — that one is aliased
            to endCashDate here because it means the closing cash date. */}
        <ReportRevisionNotice
          businessID={user.mainBusiness?.id}
          startDate={startDate}
          endDate={endDate}
          className="border-b border-gray-200"
        />

        <div className="px-4 py-5 sm:px-6 lg:px-8">
          {/* Date Range filter */}
          <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormDateRange
              startDate={startDate}
              endDate={endDate}
              onChange={(update) => setDateRange(update)}
              placeholderText={t("accounting.transactions.filter.dateRange")}
              onClear={() => setDateRange([null, null])}
              disabled={cashFlowLoading}
            />
          </div>

          {/* Grouped table */}
          <div className="flex flex-col">
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
                  <table className="min-w-full divide-y divide-gray-300">
                    {cashFlowLoading ? (
                      <TableSkeleton rows={6} columns={2} />
                    ) : (
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {cashFlowError && items.length === 0 ? (
                          <ErrorStateTable
                            otherError={cashFlowError}
                            onRetry={onLoad}
                          />
                        ) : (
                          <>
                            {operationalData.length > 0 ? (
                              <TableSection
                                categoryName="OPERATIONAL"
                                totalAmount={operationalTotal}
                              />
                            ) : (
                              <EmptyTableSection
                                categoryName="OPERATIONAL"
                                totalAmount={operationalTotal}
                              />
                            )}

                            {investmentData.length > 0 ? (
                              <TableSection
                                categoryName="INVESTMENT"
                                totalAmount={investmentTotal}
                              />
                            ) : (
                              <EmptyTableSection
                                categoryName="INVESTMENT"
                                totalAmount={investmentTotal}
                              />
                            )}

                            {financingData.length > 0 ? (
                              <TableSection
                                categoryName="FINANCING"
                                totalAmount={financingTotal}
                              />
                            ) : (
                              <EmptyTableSection
                                categoryName="FINANCING"
                                totalAmount={financingTotal}
                              />
                            )}

                            {/* Cash Changes section */}
                            <tr className="bg-gray-50">
                              <th className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap text-left">
                                {t("Cash Changes")}
                              </th>
                              <th className="px-4 py-2 text-sm text-right text-gray-700 whitespace-nowrap">
                                {t("Amount")}
                              </th>
                            </tr>
                            <tr>
                              <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                {`${t("Cash")} ${previousDate ? dayjs(previousDate).format("DD MMM YYYY") : "-"}`}
                              </td>
                              <td className="px-4 py-2 text-sm text-right text-gray-700 whitespace-nowrap">
                                <NumericFormat
                                  displayType="text"
                                  value={previousCash}
                                  thousandSeparator="."
                                  decimalSeparator=","
                                  decimalScale={2}
                                  prefix="Rp"
                                />
                              </td>
                            </tr>
                            <tr>
                              <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                {`${t("Cash")} ${endCashDate ? dayjs(endCashDate).format("DD MMM YYYY") : "-"}`}
                              </td>
                              <td className="px-4 py-2 text-sm text-right text-gray-700 whitespace-nowrap">
                                <NumericFormat
                                  displayType="text"
                                  value={endCash}
                                  thousandSeparator="."
                                  decimalSeparator=","
                                  decimalScale={2}
                                  prefix="Rp"
                                />
                              </td>
                            </tr>
                            <tr className="bg-gray-50">
                              <th className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap">
                                {`Total ${t("Cash Changes")}`}
                              </th>
                              <th className="px-4 py-2 text-sm text-right text-gray-700 whitespace-nowrap">
                                <NumericFormat
                                  displayType="text"
                                  value={cashFlow}
                                  thousandSeparator="."
                                  decimalSeparator=","
                                  decimalScale={2}
                                  prefix="Rp"
                                />
                              </th>
                            </tr>

                            {/* PSAK 2 (43) non-cash investing/financing
                                disclosure. Supplementary only — not part of the
                                cash reconciliation above. Labels come from the
                                source transaction (already localised). */}
                            {nonCashActivities.length > 0 && (
                              <>
                                <tr className="bg-gray-50">
                                  <th className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap text-left">
                                    {t("Non-Cash Activities")}
                                  </th>
                                  <th className="px-4 py-2 font-medium text-sm text-right text-gray-900 whitespace-nowrap">
                                    {t("Amount")}
                                  </th>
                                </tr>
                                {nonCashActivities.map((line, idx) => (
                                  <tr key={`noncash-${idx}`}>
                                    <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                                      {line.label}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-right text-gray-700 whitespace-nowrap">
                                      <NumericFormat
                                        displayType="text"
                                        value={line.total}
                                        thousandSeparator="."
                                        decimalSeparator=","
                                        decimalScale={2}
                                        prefix="Rp"
                                      />
                                    </td>
                                  </tr>
                                ))}
                              </>
                            )}
                          </>
                        )}
                      </tbody>
                    )}
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Download progress modal */}
        <Modal
          title="Download File"
          open={downloading}
          onClose={() => {
            if (downloadRef.current) downloadRef.current.cancel();
            setDownloading(false);
          }}
          footer={
            <div className="w-full">
              <p className="text-xs text-slate-500 mb-1">{t("Downloading")}</p>
              <div className="w-full bg-gray-200 rounded-full dark:bg-gray-700">
                <div
                  className="bg-blue-600 text-xs font-medium text-blue-100 text-center p-0.5 leading-none rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.max(4, Math.round(downloadProgress * 100))}%`,
                  }}
                >
                  {Math.round(downloadProgress * 100)}%
                </div>
              </div>
            </div>
          }
        >
          <div className="flex justify-center py-6">
            <DocumentIcon className="h-24 w-24 text-slate-400" />
          </div>
        </Modal>

        {/* Error modals */}
        {downloadDocError && (
          <ErrorDialog
            open={showDownloadErrorModal}
            error={downloadDocError}
            onClose={() => {
              setShowDownloadErrorModal(false);
              setDownloadDocError(null);
            }}
          />
        )}
        {cashFlowExportError && (
          <ErrorDialog
            open={showExportError}
            error={cashFlowExportError}
            onClose={() => {
              setShowExportError(false);
              clearExportCashflow(accountingDispatch);
            }}
          />
        )}
      </main>
    </AccountingWrapper>
  );
};

export default AccountingCashFlowReport;
